import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DeleteActionByIdParams,
  DeleteActionByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface DeleteActionByIdState
  extends MergeRequestState<DeleteActionByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.deleteActionById,
  isFetching: (state: ReduxState) => state.server.deleteActionById.isFetching,
  data: (state: ReduxState) => state.server.deleteActionById.data,
  error: (state: ReduxState) => state.server.deleteActionById.error,
};

const initialState: DeleteActionByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/deleteActionById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: DeleteActionByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.deleteActionById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerDeleteActionById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const deleteActionById: ServerDeleteActionById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
